import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { formatUsd } from "@/lib/marketplace/queries";
import { acceptCustomOfferFn, declineCustomOfferFn, listMyCustomOffers } from "@/lib/marketplace/community.functions";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/offers")({
  head: () => ({
    meta: [
      { title: "Custom offers | BeHot.Now Marketplace" },
      { name: "description", content: "Custom offers sent and received on BeHot.Now Marketplace." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: OffersPage,
});

function OffersPage() {
  const { t } = useT();
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["mp-offers", user?.id ?? null],
    enabled: Boolean(user),
    queryFn: () => listMyCustomOffers({}),
  });

  const accept = useMutation({
    mutationFn: (offerId: string) => acceptCustomOfferFn({ data: { offerId } }),
    onSuccess: (res) => {
      if (res.insufficient) {
        toast.error(t("mp.offers.toast_insufficient"));
        void navigate({ to: "/app/wallets" });
        return;
      }
      toast.success(t("mp.offers.toast_funded"));
      void navigate({ to: "/marketplace/order/$orderId", params: { orderId: res.orderId } });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("mp.offers.toast_accept_error")),
  });

  const decline = useMutation({
    mutationFn: (offerId: string) => declineCustomOfferFn({ data: { offerId } }),
    onSuccess: () => {
      toast.success(t("mp.offers.toast_closed"));
      void qc.invalidateQueries({ queryKey: ["mp-offers"] });
    },
  });

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="font-display text-3xl font-bold">{t("mp.offers.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("mp.offers.subtitle")}
        </p>

        {!user && !loading && (
          <p className="mt-8 text-sm text-muted-foreground">
            <Link to="/login" className="underline">
              {t("mp.common.sign_in")}
            </Link>{" "}
            {t("mp.offers.sign_in_suffix")}
          </p>
        )}

        {isLoading && <Skeleton className="mt-6 h-24 w-full" />}
        {data && data.length === 0 && <p className="mt-8 text-sm text-muted-foreground">{t("mp.offers.empty")}</p>}

        <ul className="mt-6 space-y-3">
          {(data ?? []).map((o) => (
            <li key={o.id} className="card-surface p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">{o.title}</p>
                <Badge variant="secondary">
                  {formatUsd(o.price_cents)} · {t("mp.offers.days_suffix", { n: o.delivery_days })} · {t("mp.offers.rev_suffix", { n: o.revisions })}
                </Badge>
              </div>
              {o.description && <p className="mt-2 text-sm text-muted-foreground">{o.description}</p>}
              <p className="mt-2 text-xs text-muted-foreground">
                {o.role === "buyer"
                  ? t("mp.offers.from_seller", { name: o.seller?.display_name || o.seller?.handle || t("mp.common.seller") })
                  : t("mp.offers.to_buyer", { name: o.buyer?.display_name || o.buyer?.handle || t("mp.common.buyer") })}{" "}
                · {o.status}
              </p>
              {o.status === "sent" && (
                <div className="mt-3 flex gap-2">
                  {o.role === "buyer" && (
                    <Button size="sm" variant="hot" disabled={accept.isPending} onClick={() => accept.mutate(o.id)}>
                      {t("mp.offers.accept_fund")}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" disabled={decline.isPending} onClick={() => decline.mutate(o.id)}>
                    {o.role === "buyer" ? t("mp.offers.decline") : t("mp.offers.withdraw")}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </MarketplaceGate>
  );
}
